import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MessageCircle, MapPin, ShieldCheck, Home, Building2, Sparkles, Users } from "lucide-react";
import { formatPrice, resolveHeroImage, NoImagePlaceholder } from "@/components/ProjectCard";

const CONSTRUCTION_BADGE_STYLES = {
  PRE_LAUNCH: "bg-sg-gold text-sg-navy",
  UNDER_CONSTRUCTION: "bg-sg-navy text-sg-gold border border-sg-gold",
  READY_TO_MOVE: "bg-emerald-600 text-white",
  RESALE: "bg-sg-mute text-white",
  UNKNOWN: "bg-sg-line text-sg-navy",
};

const KIND_LABEL = {
  agent_listing: "Agent Listing",
  master_project: "Verified Launch",
};

function Badge({ status, label, testId }) {
  const cls = CONSTRUCTION_BADGE_STYLES[status] || CONSTRUCTION_BADGE_STYLES.UNKNOWN;
  return (
    <span
      className={`chip ${cls} font-medium tracking-wide`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

function AgentAttribution({ item }) {
  // Show attribution any time we have an agent linked — this now includes:
  //  • agent_listing rows (broker's own inventory)
  //  • master_project rows that were assigned to an agent during admin ingest
  // If no agent is linked, render nothing so the card falls back to the default
  // "INPropPortal · Verified Launch" chip in the header.
  if (!item.agent_slug) return null;
  const testId = item.listing_id
    ? `buy-attribution-${item.listing_id}`
    : `buy-attribution-${item.project_id}`;
  return (
    <Link
      to={`/agent/${item.agent_slug}`}
      className="mt-4 flex items-center gap-3 rounded-2xl border border-sg-line bg-sg-cream-soft/60 px-3 py-2 hover:border-sg-gold transition-colors"
      data-testid={testId}
    >
      {item.agent_picture ? (
        <img
          src={item.agent_picture}
          alt={item.agent_name}
          className="w-9 h-9 rounded-full object-cover"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-sg-navy text-sg-gold flex items-center justify-center text-sm font-semibold">
          {(item.agent_name || "?").slice(0, 1)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-sg-gold-deep font-semibold">
          Contact Authorized Agent
        </div>
        <div className="text-sm font-semibold text-sg-navy truncate">{item.agent_name}</div>
        {item.agent_maharera && (
          <div className="font-mono text-[10px] text-sg-mute truncate">
            MahaCRITI · {item.agent_maharera}
          </div>
        )}
      </div>
      <span className="overline text-sg-gold-deep">View portal →</span>
    </Link>
  );
}

// Subtle "+ N other authorized agents" pill rendered under the attribution card.
// Clicking it opens a Popover listing every alternate agent — each a link back
// to their branded microsite. Rendered only when the marketplace card is shared
// by 2+ agents (the primary + at least one alternate).
function AlsoListedBadge({ item }) {
  const partners = item.also_listed_by || [];
  if (!partners.length) return null;
  const testIdBase = item.listing_id || item.project_id || "unknown";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-sg-gold/40 bg-sg-gold/10 text-sg-navy px-3 py-1 text-[11px] font-medium hover:bg-sg-gold/20 hover:border-sg-gold transition-colors"
          data-testid={`buy-also-listed-btn-${testIdBase}`}
          aria-label={`${partners.length} more authorized agents`}
        >
          <Users className="w-3 h-3 text-sg-gold-deep" />
          + {partners.length} other authorized agent{partners.length === 1 ? "" : "s"}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0 rounded-2xl border-sg-line shadow-lg"
        data-testid={`buy-also-listed-popover-${testIdBase}`}
      >
        <div className="px-4 py-3 border-b border-sg-line bg-sg-cream-soft">
          <div className="overline text-sg-gold-deep">ALSO LISTED BY</div>
          <div className="text-xs text-sg-mute mt-0.5">{partners.length} additional authorized {partners.length === 1 ? "agent" : "agents"}</div>
        </div>
        <div className="py-1 max-h-72 overflow-y-auto">
          {partners.map((p) => (
            <Link
              key={p.slug}
              to={`/agent/${p.slug}`}
              className="flex items-center gap-3 px-4 py-2 hover:bg-sg-cream-soft transition-colors"
              data-testid={`buy-also-listed-agent-${testIdBase}-${p.slug}`}
            >
              {p.profile_picture ? (
                <img
                  src={p.profile_picture}
                  alt={p.name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-sg-navy text-sg-gold flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {(p.name || "?").slice(0, 1)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-sg-navy truncate">{p.name}</div>
                {p.maharera_number && (
                  <div className="font-mono text-[10px] text-sg-mute truncate">
                    MahaCRITI · {p.maharera_number}
                  </div>
                )}
              </div>
              <span className="text-[10px] text-sg-gold-deep font-medium whitespace-nowrap">View portal →</span>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BuyCard({ item }) {
  const isMaster = item.kind === "master_project";
  const title = isMaster ? item.project_name : `${item.bhk || ""} · ${item.property_type || "Apartment"}`;
  const subtitle = isMaster ? item.developer : (item.unit_variant || item.notes || "Resale / secondary");
  // Hero-priority: `image_urls[0]` > `thumbnail_url` > branded placeholder.
  // `resolveHeroImage` handles both — falls back to null so we can render the
  // shared `<NoImagePlaceholder />` when neither source has an image.
  const heroSrc = resolveHeroImage(item);
  const detailHref = isMaster
    ? `/project/${item.project_id}`
    : (item.agent_slug ? `/agent/${item.agent_slug}` : "#");

  const testId = isMaster
    ? `buy-card-master-${item.project_id}`
    : `buy-card-agent-${item.listing_id}`;

  const waNum = null; // agents' WA is on their portal — main Buy card routes to portal, not direct WA
  return (
    <div className="sg-card flex flex-col" data-testid={testId}>
      <Link to={detailHref} className="block relative aspect-[16/9] overflow-hidden bg-sg-cream-soft">
        {heroSrc ? (
          <img
            src={heroSrc}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <NoImagePlaceholder />
        )}
        <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
          <Badge
            status={item.construction_status}
            label={item.construction_status_label || KIND_LABEL[item.kind]}
            testId={`${testId}-status`}
          />
          <span className="chip bg-white/95 text-sg-navy border border-sg-line font-medium">
            {isMaster ? (
              <><Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" />NEW LAUNCH</>
            ) : (
              <><Home className="w-3 h-3 inline mr-1 -mt-0.5" />AGENT LISTING</>
            )}
          </span>
        </div>
        {item.locality && (
          <span className="absolute top-4 right-4 chip bg-sg-navy/90 text-sg-gold-soft border border-sg-gold/40">
            <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />{item.locality}
          </span>
        )}
      </Link>
      <div className="p-6 flex-1 flex flex-col">
        <div className="overline text-sg-gold-deep">{subtitle}</div>
        <Link to={detailHref} className="block">
          <h3
            className="font-display text-xl mt-1 text-sg-navy hover:text-sg-gold-deep transition-colors"
            data-testid={`${testId}-title`}
          >
            {title}
          </h3>
        </Link>

        {(isMaster ? item.configurations : [item.bhk, item.unit_variant].filter(Boolean)) && (
          <div className="mt-3 flex items-center justify-between border-t border-dashed border-sg-line pt-3 text-sm text-sg-mute">
            <span className="font-mono truncate">
              {(isMaster ? item.configurations : [item.bhk, item.unit_variant].filter(Boolean)).join(" · ")}
            </span>
            {item.external_rera_number && (
              <span className="overline text-sg-gold-deep truncate ml-3">RERA · {item.external_rera_number}</span>
            )}
          </div>
        )}

        <div className="mt-auto pt-4 flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-sg-mute">
              {isMaster ? "Starting from" : "Ask"}
            </div>
            <div className="rupee text-3xl text-sg-navy mt-0.5">{formatPrice(item.price_lakhs)}</div>
          </div>
          <Link
            to={detailHref}
            className="inline-flex items-center gap-2 rounded-full bg-sg-navy text-white px-4 py-3 text-sm hover:bg-sg-navy-700 transition-colors min-h-[44px]"
            data-testid={`${testId}-cta`}
          >
            {isMaster ? (
              <><Building2 className="w-4 h-4 text-sg-gold" /> View Project</>
            ) : (
              <><MessageCircle className="w-4 h-4 text-sg-gold" /> Talk to Agent</>
            )}
          </Link>
        </div>

        <AgentAttribution item={item} />
        <AlsoListedBadge item={item} />

        {isMaster && item.market_trend && (
          <div className="mt-4 text-xs text-sg-mute italic border-t border-dashed border-sg-line pt-3">
            <span className="not-italic font-semibold font-mono text-sg-navy">{item.market_trend.yoy}</span>
            <span className="mx-1">·</span>{item.market_trend.summary}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PublicBuy() {
  const [data, setData] = useState({ items: [], count: 0, construction_status_options: [], property_source_options: [] });
  const [loading, setLoading] = useState(true);
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [filters, setFilters] = useState({
    property_source: "all",
    construction_status: "any",
    zone: "any",
    locality: "any",
    bhk: "any",
    q: "",
  });

  useEffect(() => {
    api.get("/localities").then(({ data }) => setLocalities(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.property_source !== "all") params.property_source = filters.property_source;
    if (filters.construction_status !== "any") params.construction_status = filters.construction_status;
    if (filters.zone !== "any") params.zone = filters.zone;
    if (filters.locality !== "any") params.locality = filters.locality;
    if (filters.bhk !== "any") params.bhk = filters.bhk;
    if (filters.q) params.q = filters.q;
    params.limit = 60;
    api.get("/buy", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [filters]);

  const localityChoices = useMemo(() => {
    if (filters.zone === "Pune West") return localities["Pune West"] || [];
    if (filters.zone === "Pune East") return localities["Pune East"] || [];
    return [...(localities["Pune West"] || []), ...(localities["Pune East"] || [])];
  }, [filters.zone, localities]);

  const setF = (k) => (v) => setFilters((prev) => ({ ...prev, [k]: v, ...(k === "zone" ? { locality: "any" } : {}) }));

  const heroCount = data.count;
  const globalMasterCount = data.items.filter(i => i.kind === "master_project").length;
  const globalAgentCount = data.items.filter(i => i.kind === "agent_listing").length;

  return (
    <PublicLayout>
      <section className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-sg-gold mb-5">MARKETPLACE · PUNE</div>
          <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] max-w-3xl" data-testid="buy-hero-title">
            Every Pune deal in one place.
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl">
            Verified new launches from Pune's most active developers, plus{" "}
            <span className="text-sg-gold font-medium">Portal-Global agent listings</span> — each attributed to a
            MahaCRITI-registered advisor. Tap through to see full inventory on their branded portal.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            <span className="chip bg-white/10 text-white border border-white/20">
              <ShieldCheck className="w-3 h-3 inline mr-1 -mt-0.5" />
              MahaCRITI-aligned
            </span>
            <span className="chip bg-sg-gold text-sg-navy">
              {heroCount} live results
            </span>
            <span className="chip bg-white/10 text-white border border-white/20">
              {globalMasterCount} new launches · {globalAgentCount} agent listings
            </span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-10">
        <div className="bg-white border border-sg-line rounded-3xl p-5 flex flex-col md:flex-row md:items-center gap-3 shadow-sm">
          <Input
            placeholder="Search project, developer, locality…"
            value={filters.q}
            onChange={(e) => setF("q")(e.target.value)}
            className="h-11 md:max-w-xs rounded-full border-sg-line"
            data-testid="buy-search"
          />
          <Select value={filters.property_source} onValueChange={setF("property_source")}>
            <SelectTrigger className="h-11 md:w-52 rounded-full border-sg-line" data-testid="buy-property-source">
              <SelectValue placeholder="All property sources" />
            </SelectTrigger>
            <SelectContent>
              {(data.property_source_options || []).map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`buy-source-opt-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.construction_status} onValueChange={setF("construction_status")}>
            <SelectTrigger className="h-11 md:w-52 rounded-full border-sg-line" data-testid="buy-construction-status">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any" data-testid="buy-status-opt-any">Any construction status</SelectItem>
              {(data.construction_status_options || []).map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`buy-status-opt-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.zone} onValueChange={setF("zone")}>
            <SelectTrigger className="h-11 md:w-40 rounded-full border-sg-line" data-testid="buy-zone">
              <SelectValue placeholder="All corridors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All corridors</SelectItem>
              <SelectItem value="Pune West">Pune West</SelectItem>
              <SelectItem value="Pune East">Pune East</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.locality} onValueChange={setF("locality")}>
            <SelectTrigger className="h-11 md:w-48 rounded-full border-sg-line" data-testid="buy-locality">
              <SelectValue placeholder="All localities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All localities</SelectItem>
              {localityChoices.map((l) => (
                <SelectItem key={l} value={l} data-testid={`buy-locality-opt-${l.replace(/\s+/g, '-').toLowerCase()}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-sg-mute md:ml-auto font-mono px-2" data-testid="buy-count">
            {loading ? "Loading…" : `${data.count} results`}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-sg-mute animate-pulse" data-testid="buy-loading">Loading marketplace…</div>
        ) : data.items.length === 0 ? (
          <div
            className="text-sg-mute py-16 text-center border border-dashed border-sg-line rounded-3xl"
            data-testid="buy-empty"
          >
            No listings match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="buy-grid">
            {data.items.map((item) => (
              <BuyCard
                key={item.kind === "master_project" ? `mp-${item.project_id}` : `al-${item.listing_id}`}
                item={item}
              />
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
